import React from 'react';
import { Wind, Anchor, Activity, Camera, RefreshCw, Mountain, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * Compact homepage preview of the Apex Six framework.
 *
 * Replaces `ApexSixMethod` on the homepage only (the full deep-dive
 * `ApexSixMethod` component lives unchanged on `/method`). This version is
 * intentionally short: 6 named tiles (no blurbs), a one-line intro, and a
 * cross-link to `/method`. Goal — keep the homepage to a gateway-density.
 *
 * Phase 22 (information-architecture restructure).
 */
const tiles = [
  { n: '01', Icon: Wind,     name: 'The 3-Breath Reset' },
  { n: '02', Icon: Anchor,   name: 'The ABC Confidence Stack' },
  { n: '03', Icon: Activity, name: 'Green-Yellow-Red Awareness' },
  { n: '04', Icon: Camera,   name: 'The Camera Drill' },
  { n: '05', Icon: RefreshCw,name: 'Bounce-Back 3R' },
  { n: '06', Icon: Mountain, name: 'The Performance Pyramid' },
];

const fadeIn = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
};

export const ApexSixPreview = () => {
  return (
    <section
      className="relative py-16 sm:py-20 bg-gradient-to-br from-[#0a0d0e] via-[#0f1416] to-[#0a0d0e] text-white overflow-hidden"
      data-testid="apex-six-preview-section"
    >
      <div className="relative max-w-[1120px] mx-auto px-5 sm:px-6">
        <motion.div {...fadeIn} className="mb-10">
          <div className="font-mono-accent uppercase tracking-[0.25em] text-[10px] text-[#4FBCC7] font-bold mb-3">
            The Method
          </div>
          <h2 className="font-heading text-[1.85rem] sm:text-[2.25rem] font-bold tracking-tight max-w-[24ch] mb-3">
            Six protocols. One playbook for the mental side of the game.
          </h2>
          <p className="text-[15px] text-white/55 max-w-[60ch] leading-[1.55]">
            The Apex Six is the framework Coach Lewis teaches in every package — the repeatable system athletes use to compete with confidence and perform under pressure.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-8">
          {tiles.map((t, i) => {
            const Icon = t.Icon;
            return (
              <motion.div
                key={t.n}
                {...fadeIn}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="group p-4 sm:p-5 rounded-xl bg-white/[0.035] ring-1 ring-white/10 hover:ring-[#4FBCC7]/40 hover:bg-white/[0.06] transition-all"
                data-testid={`apex-six-preview-tile-${t.n}`}
              >
                <div className="flex items-center gap-3 mb-1.5">
                  <div className="flex-shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#004C54] ring-1 ring-[#4FBCC7]/30">
                    <Icon className="h-4 w-4 text-white" />
                  </div>
                  <span className="font-mono-accent text-[9.5px] uppercase tracking-[0.2em] text-[#4FBCC7]/80 font-bold">
                    {t.n}
                  </span>
                </div>
                <h3 className="font-heading text-[14.5px] sm:text-[15.5px] font-semibold text-white leading-tight">
                  {t.name}
                </h3>
              </motion.div>
            );
          })}
        </div>

        <a
          href="/method"
          className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#4FBCC7] hover:text-white transition-colors"
          data-testid="apex-six-preview-method-link"
        >
          Read the full method <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </div>
    </section>
  );
};

export default ApexSixPreview;
